<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8 tw-tracking-wider">
      {{ $t('adm.agency.users.create_button') }}
    </h1>
    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-flex tw-flex-col tw-gap-2 tw-mt-3"
      @submit.native.prevent="submitForm"
    >
      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.first_name')"
          :error="errors?.first_name?.[0]"
          :class="form.first_name ? 'has-value' : ''"
          prop="first_name"
        >
          <el-input
            v-model="form.first_name"
          />
        </i-form-item>
        <i-form-item
          :label="$t('common.fields.last_name')"
          :error="errors?.last_name?.[0]"
          :class="form.last_name ? 'has-value' : ''"
          prop="last_name"
        >
          <el-input
            v-model="form.last_name"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.email')"
          :error="errors?.email?.[0]"
          :class="form.email ? 'has-value' : ''"
          prop="email"
        >
          <el-input
            v-model="form.email"
            type="email"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.gender')"
          :class="form.gender ? 'has-value' : ''"
          prop="gender"
        >
          <el-select
            v-model="form.gender"
            filterable
            placeholder=""
          >
            <el-option
              v-for="item in genders"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </i-form-item>
        <i-form-item
          :label="$t('common.fields.role')"
          :class="form.role ? 'has-value' : ''"
          prop="role"
        >
          <el-select
            v-model="form.role"
            filterable
            placeholder=""
          >
            <el-option
              v-for="item in internalUserRoles"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2">
        <i-form-item
          :label="$t('common.fields.phone')"
          :error="errors?.phone_primary?.[0]"
          :class="form.phone_primary ? 'has-value' : ''"
          prop="phone_primary"
        >
          <el-input
            v-model="form.phone_primary"
            type="tel"
          />
        </i-form-item>

        <i-form-item
          :label="$t('common.fields.phone')"
          :error="errors?.phone_secondary?.[0]"
          :class="form.phone_secondary ? 'has-value' : ''"
          prop="phone_secondary"
        >
          <el-input
            v-model="form.phone_secondary"
            type="tel"
          />
        </i-form-item>
      </div>

      <div class="tw-flex tw-items-start tw-gap-2" />
    </el-form>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { getValidationError } from '@/helpers';

const FORM_BASE = {
  first_name: '', // required, maxLength: 30
  last_name: '', //  maxLength: 150
  email: '',// required
  gender: '',// required
  role: '',// required
  phone_primary: '', // not required, but if entered maxLength: 32
  phone_secondary: '', // not required, but if entered maxLength: 32
};

export default {
  name: 'AddInternalUserDialog',
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      errors: {},
      form: _cloneDeep(FORM_BASE),
      formRules: {
        first_name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 30, message: 'Max 30 characters', trigger: 'blur' },
        ],
        last_name: [
          { required: true, message: 'Required', trigger: 'blur' },
          { max: 150, message: 'Max 150 characters', trigger: 'blur' },
        ],
        email: [
          { required: true, message: 'Required', trigger: 'blur' },
          { type: 'email', message: 'Please input correct email address', trigger: 'blur' },
        ],
        gender: [
          { required: true, message: 'Required', trigger: ['change', 'blur']  },
        ],
        role: [
          { required: true, message: 'Required', trigger: ['change', 'blur']  },
        ],
        phone_primary: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
        phone_secondary: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      genders: (state) => state.v2.shared.enums.enums.genders,
      internalUserRoles: (state) => state.v2.shared.enums.internal_user_roles,
    }),
  },

  methods: {
    opened() {
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.form = _cloneDeep(FORM_BASE);
      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/admin/cms/internal/users/addInternalUser', {
          payload: this.form,
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
